import React, { useState, useEffect } from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import { X } from "lucide-react";
import { externalApiTypes } from "../../../../Config/constants";

export default function HowToUseAPIDialog({ dialogOpen, closeDialog, apiTypeId, apiUrl, apiBodyItems, ...props }) {
  const classes = useStyles();
  const excludeStopwatchKeys = ["stopwatchReference", "startTime", "stopTime", "singleTimer"];
  const excludeNumericKeys = ["keyValue", "measurementTime", "value"];

  const [copiedToClipboard, setCopiedToClipboard] = useState(false); // Confirmation that link is succesfully copied to clipboard

  // Format bodyItems
  const bodyItems = apiBodyItems.reduce((acc, item, index) => {
    acc[item.itemName] = `value ${index + 1}`;
    return acc;
  }, {});

  // Example code
  const jsCodeDataAPI = `fetch('${apiUrl}', {
method: 'POST',
headers: {
    'Content-Type': 'application/json',
},
body: JSON.stringify(
    {
        "keyValue": "123ABC", // optional key value (can be used to link to an order number)
        "measurementTime": "14/01/2025 13:30:12", // optional, if null the measurement will be logged at the current time
        "customReference": "abc-123987", // Optional: if you want to provide your own reference, use this ${Object.entries(bodyItems)
          .map(([key, value]) => `        "${key}": "${value}"`)
          .join(",\n")}
    }
)
})
.then(response => response.json())
.then(data => {
    console.log('Server response:', data);
})
.catch(error => {
    console.error('An error has occured:', error);
});`;

  const jsCodeStopwatchAPI = `fetch('${apiUrl}', {
method: 'POST',
headers: {
    'Content-Type': 'application/json',
},
body: JSON.stringify(
    {
        "stopwatchReference": "00000000-0000-0000-0000-000000000000", // if you are starting a new stopwatch, this should be left empty
        "customReference": "ORD-1001", // Optional: if you want to provide your own reference, use this
        "startTime": "14/01/2025 13:30:12", // the time you want to start the stopwatch
        "stopTime": "14/01/2025 13:42:41", // the time you want to stop the stopwatch (should always be later then startTime)
        "useRefAsKey": "True" // or false, will use reference as a key for the measurement (defaults to false) 
        "singleTimer": "True", // or false, when true starting a new timer will stop other running timers on this API
        "keyValue": "123ABC", // optional key value (can be used to link to an order number). If you use useRefAsKey, this shouldn't be used ${Object.entries(bodyItems)
          .filter(([key]) => !excludeStopwatchKeys.includes(key))
          .map(([key, value]) => `        "${key}": "${value}"`)
          .join(",\n")}
    }
)
})
.then(response => response.json())
.then(data => {
    console.log('Server response:', data);
})
.catch(error => {
    console.error('An error has occured:', error);
});`;

  const jsCodeNumericAPI = `fetch('${apiUrl}', {
method: 'POST',
headers: {
    'Content-Type': 'application/json',
},
body: JSON.stringify(
    {
        "value": "42", // the numeric value you want to measure (can be negative)
        "customReference": "ORD-1001", // Optional: if you want to provide your own reference, use this
        "measurementTime": "14/01/2025 13:30:12", // optional, if null the measurement will be logged at the current time
        "keyValue": "123ABC" // optional key value (can be used to link to an order number) 
${Object.entries(bodyItems)
  .filter(([key]) => !excludeNumericKeys.includes(key))
  .map(([key, value]) => `        "${key}": "${value}"`)
  .join(",\n")}
    }
)
})
.then(response => response.json())
.then(data => {
    console.log('Server response:', data);
})
.catch(error => {
    console.error('An error has occured:', error);
});`;

  const copyCodeToClipboard = () => {
    // Copy code to clipboard
    navigator.clipboard
      .writeText(apiTypeId === externalApiTypes.DATA ? jsCodeDataAPI : jsCodeStopwatchAPI)
      .then(() => {
        setCopiedToClipboard(true);

        // Set copiedToClipboard back to false after 2 seconds
        setTimeout(() => {
          setCopiedToClipboard(false);
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
      });
  };

  return (
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" onClose={closeDialog} classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
          How to use the API?
        </DialogTitle>
        <button onClick={() => closeDialog()} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          {/* Data API */}
          {apiTypeId === externalApiTypes.DATA && (
            <div className={classes.dialog__textContainer}>
              <p className={classes.textContainer__paragraph}>Below you can see an example of an API call in JavaScript specifically for this API you created.</p>
              <p className={classes.textContainer__paragraph}>Make sure to change the values you pass with your own values.</p>

              <h3 className={classes.textContainer__subtitle}>measurementTime</h3>
              <p className={classes.textContainer__paragraph}>Optional. If null the measurement will be logged at the current time</p>

              <h3 className={classes.textContainer__subtitle}>customReference</h3>
              <p className={classes.textContainer__paragraph}>
                Optional. If you want to provide your own reference to update values later on, use this. This could be an order number or something similar.
              </p>
            </div>
          )}
          {/* Stopwatch API */}
          {apiTypeId === externalApiTypes.STOPWATCH && (
            <div className={classes.dialog__textContainer}>
              <p className={classes.textContainer__paragraph}>
                This is an API of the type “Stopwatch”. This means that you can start and stop your stopwatch. The way you use this type of API is a little bit different from a “Data” API, but if you
                read through the explanation, this should't be a problem.
              </p>
              <h3 className={classes.textContainer__subtitle}>Reference</h3>
              <p className={classes.textContainer__paragraph}>
                A reference is used to make sure you can stop a stopwatch you have started earlier. You pass the reference along with your stop-time to finish the stopwatch measurement.
              </p>
              <p className={classes.textContainer__listTitle}>You have 2 option regarding references: </p>
              <ul>
                <li className={classes.textContainer__listItem}>
                  You can leave the stopwatchReference empty when starting a new stopwatch. In this case, you will get a stopwatchReference in the response
                </li>
                <li className={classes.textContainer__listItem}>
                  You provide your own customReference when starting a new stopwatch. In this case you can use your customReference to both start and stop the stopwatch
                </li>
              </ul>
              <h3 className={classes.textContainer__subtitle}>startTime</h3>
              <p className={classes.textContainer__paragraph}>
                This is obviously the time your stopwatch starts. pass it in the following format: “dd:mm:yyyy hh:mm:ss”. When you want to stop the stopwatch, you can leave the startTime empty. If you
                pass a startTime along with a stopwatchReference, the startTime will be overwritten.
              </p>
              <h3 className={classes.textContainer__subtitle}>stopTime</h3>
              <p className={classes.textContainer__paragraph}>
                This is the time your stopwatch stops. You can leave this empty when starting a new stopwatch. If you do pass a stopTime when starting a stopwatch, the stopwatch will be finished.
              </p>
              <h3 className={classes.textContainer__subtitle}>keyValue</h3>
              <p className={classes.textContainer__paragraph}>If you want to add a key to your measurement you can pass it along right here.</p>
              <h3 className={classes.textContainer__subtitle}>useRefAsKey</h3>
              <p className={classes.textContainer__paragraph}>
                If you pass along your own reference, it could be nice to set this as a key for the measurement. Your reference could for example be an order number. If you use this, you don't need to
                pass keyValue anymore since the reference will be used.
              </p>
              <h3 className={classes.textContainer__subtitle}>singleTimer</h3>
              <p className={classes.textContainer__paragraph}>
                When set to true, starting a new timer will automatically stop any other running timers on this API. This ensures only one timer is running at a time. Set to false or omit if you want to allow multiple concurrent timers.
              </p>
            </div>
          )}
          {/* Numeric API */}
          {apiTypeId === externalApiTypes.NUMERIC && (
            <div className={classes.dialog__textContainer}>
              <p className={classes.textContainer__paragraph}>The numeric API allows you to measure numeric values.</p>

              <h3 className={classes.textContainer__subtitle}>value</h3>
              <p className={classes.textContainer__paragraph}>Value you want to measure. Can be negative</p>

              <h3 className={classes.textContainer__subtitle}>measurementTime</h3>
              <p className={classes.textContainer__paragraph}>Optional. If null the measurement will be logged at the current time</p>

              <h3 className={classes.textContainer__subtitle}>customReference</h3>
              <p className={classes.textContainer__paragraph}>
                Optional. If you want to provide your own reference to update values later on, use this. This could be an order number or something similar.
              </p>

              <h3 className={classes.textContainer__subtitle}>keyValue</h3>
              <p className={classes.textContainer__paragraph}>This is an optional string that can be used to tag or group related measurements — e.g., per order, user, or process.</p>
            </div>
          )}

          <div className={classes.functionExampleContainer}>
            <button className={classes.copyButton} onClick={() => copyCodeToClipboard()}>
              {copiedToClipboard ? <p className={classes.copiedText}>Copied!</p> : ""}
              <p className={`${copiedToClipboard ? classes.copied : ""}`}> Copy code</p>
            </button>
            <SyntaxHighlighter
              language="javascript"
              customStyle={{
                background: "transparent", // Makes the background transparent
                padding: "16px", // Removes padding
                margin: "0", // Removes margin
                paddingBottom: "8px",
                paddingTop: "24px",
              }}
            >
              {apiTypeId === externalApiTypes.DATA ? jsCodeDataAPI : apiTypeId === externalApiTypes.STOPWATCH ? jsCodeStopwatchAPI : jsCodeNumericAPI}
            </SyntaxHighlighter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    maxWidth: "800px !important",
    width: "800px !important",
    maxHeight: "80vh !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  textContainer__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "bold",

    color: "#0C0E29",

    margin: "0px 0px 4px 0px",
  },

  textContainer__paragraph: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  textContainer__listTitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 8px 0px",
  },

  textContainer__listItem: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 8px 0px",
  },

  functionExampleContainer: {
    width: "100%",
    boxSizing: "border-box",

    backgroundColor: "#F8F8F8",

    borderRadius: "8px",
    padding: "8px",

    overflow: "hidden",
    position: "relative",

    "& > pre": {
      "&::-webkit-scrollbar": {
        width: "8px" /* Adjust width for vertical scroll */,
        height: "8px" /* Adjust height for horizontal scroll */,
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(0, 0, 0, 0.5)" /* A semi-transparent thumb */,
        borderRadius: "4px" /* Rounded corners */,
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.7)" /* Darker thumb on hover */,
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent" /* Make the track transparent */,
      },
    },
  },

  copyButton: {
    position: "absolute",
    top: "8px",
    right: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    border: "none",
    borderRadius: "4px",
    backgroundColor: "#EDEDED",

    color: "#0C0E29",

    padding: "4px 8px",

    transition: "background-color ease-in-out 0.2s",

    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#DCDCDC",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },

    "& p": {
      margin: 0,
    },
  },

  copied: {
    opacity: 0,
  },

  copiedText: {
    opacity: 1,
    color: "#0C0E29",
    position: "absolute",
  },
}));
